import React, { useEffect, useRef, useState } from "react";
import { AlertCircle, CheckCircle2, FileText, FolderOpen, Image as ImageIcon, RefreshCw, Trash2, Upload } from "lucide-react";
import TiltCard from "./TiltCard";
import { hasApi, NO_BACKEND_MESSAGE } from "../lib/api";
import {
  CIRCULAR_CATEGORIES,
  DRIVE_NOT_CONNECTED,
  GALLERY_ALBUMS,
  MAX_GALLERY_BYTES,
  MAX_GALLERY_LABEL,
  deleteSiteFile,
  fetchDriveStatus,
  fetchSiteCirculars,
  fetchSiteGallery,
  isGalleryImageFile,
  isTransientServerError,
  syncWebsiteGalleryPhotos,
  uploadSiteFile,
} from "../lib/siteDrive";

export default function SiteDriveAdmin({ kind, allowEdit }) {
  const isCirculars = kind === "circulars";
  const options = isCirculars ? CIRCULAR_CATEGORIES : GALLERY_ALBUMS;
  const [choice, setChoice] = useState(options[0].value);
  const [files, setFiles] = useState([]);
  const [drive, setDrive] = useState({ connected: false, error: null });
  const [status, setStatus] = useState("loading");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [title, setTitle] = useState("");
  const inputRef = useRef();
  const autoSyncRef = useRef(false);

  const load = async () => {
    setStatus("loading");
    try {
      const [listing, info] = await Promise.all([
        isCirculars ? fetchSiteCirculars() : fetchSiteGallery(),
        fetchDriveStatus(),
      ]);
      setFiles(listing.files || []);
      setDrive({
        connected: Boolean(info.connected || listing.connected),
        error: listing.error || info.error || null,
      });
      setStatus("ready");
      return listing;
    } catch (err) {
      setFiles([]);
      setDrive({ connected: false, error: err?.message || DRIVE_NOT_CONNECTED });
      setStatus("ready");
      return null;
    }
  };

  const syncWebsite = async ({ silent } = {}) => {
    if (isCirculars || !allowEdit) return;
    setBusy(true);
    if (!silent) setMsg(null);
    try {
      let uploaded = 0;
      let skipped = 0;
      let guard = 0;
      let last = null;
      while (guard < 40) {
        const result = await syncWebsiteGalleryPhotos();
        last = result;
        if (!result.ok) {
          setMsg({ ok: false, text: result.detail || "Could not sync website photographs." });
          return;
        }
        uploaded += Number(result.uploaded || 0);
        skipped += Number(result.skipped || 0);
        if (result.done) break;
        guard += 1;
      }
      await load();
      setMsg({
        ok: true,
        text: uploaded
          ? `Website photographs are in Drive (${uploaded} uploaded, ${skipped} already there).`
          : (last && last.missing
            ? "Drive is ready. Website photograph files were not on this server yet — they will sync after Manual Deploy copies them."
            : "Website photographs are already in Drive."),
      });
    } catch (err) {
      setMsg({ ok: false, text: err?.message || "Could not sync website photographs." });
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    let live = true;
    (async () => {
      const listing = await load();
      if (!live || isCirculars || !allowEdit || autoSyncRef.current) return;
      autoSyncRef.current = true;
      if (listing && listing.connected && !(listing.files || []).length) {
        await syncWebsite({ silent: true });
      }
    })();
    return () => { live = false; };
  }, [kind]);

  const upload = async (e) => {
    const list = Array.from(e.target.files || []);
    if (!list.length) {
      setMsg({ ok: false, text: isCirculars ? "Choose a circular or G.O. file first." : "Choose a photograph first." });
      return;
    }
    if (!allowEdit) {
      setMsg({ ok: false, text: "Your account is set to view only. Ask the administrator for edit access." });
      return;
    }
    if (!hasApi()) { setMsg({ ok: false, text: NO_BACKEND_MESSAGE }); return; }
    for (const f of list) {
      if (!isCirculars && !isGalleryImageFile(f)) {
        setMsg({ ok: false, text: "Please choose a photograph (JPEG, PNG, WebP or GIF)." });
        if (inputRef.current) inputRef.current.value = "";
        return;
      }
      if (!isCirculars && f.size > MAX_GALLERY_BYTES) {
        setMsg({ ok: false, text: `That photograph is larger than ${MAX_GALLERY_LABEL}. Use a smaller JPG.` });
        if (inputRef.current) inputRef.current.value = "";
        return;
      }
    }
    setBusy(true);
    setMsg(null);
    try {
      const uploaded = [];
      for (let i = 0; i < list.length; i += 1) {
        const f = list[i];
        const fd = new FormData();
        fd.append("file", f, f.name || (isCirculars ? "document.pdf" : "photo.jpg"));
        if (isCirculars) fd.append("category", choice);
        else fd.append("album", choice);
        const stem = (f.name || "").replace(/\.[^.]+$/, "");
        const caption = title.trim()
          ? (list.length > 1 ? `${title.trim()} (${i + 1})` : title.trim())
          : stem;
        if (caption) fd.append("title", caption);
        const path = isCirculars ? "/api/circulars/upload" : "/api/gallery/upload";
        const result = await uploadSiteFile(path, fd);
        if (!result.ok) {
          setMsg({ ok: false, text: result.detail || "Upload failed" });
          return;
        }
        if (result.file && result.file.id) uploaded.push(result.file);
      }
      if (uploaded.length) {
        setFiles((prev) => {
          const seen = new Set(uploaded.map((row) => row.id));
          return [...uploaded, ...prev.filter((row) => !seen.has(row.id))];
        });
      }
      const label = options.find((o) => o.value === choice)?.label || choice;
      setMsg({ ok: true, text: `${list.length} file(s) added to ${label} and are now on the public website.` });
      if (inputRef.current) inputRef.current.value = "";
      setTitle("");
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err?.message || "Upload failed" });
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const remove = async (f) => {
    if (!allowEdit) return;
    if (!window.confirm(`Remove “${f.title || f.name}” from the public website?`)) return;
    setBusy(true);
    setMsg(null);
    try {
      const path = isCirculars ? `/api/circulars/${f.id}` : `/api/gallery/${f.id}`;
      const result = await deleteSiteFile(path);
      if (!result.ok) {
        setMsg({ ok: false, text: result.detail || "Could not remove the file." });
        return;
      }
      setFiles((prev) => prev.filter((row) => row.id !== f.id));
      setMsg({ ok: true, text: "Removed from the website." });
      await load();
    } catch (err) {
      setMsg({ ok: false, text: err?.message || "Could not remove the file." });
    } finally {
      setBusy(false);
    }
  };

  const heading = isCirculars ? "Circulars & Government Orders" : "Gallery photographs";
  const help = isCirculars
    ? "Give the circular a title, then upload a PDF or scan into Circulars, GOs or Notifications. That title is what the public list shows."
    : `Give the photograph a name tag, then upload a JPG/PNG under ${MAX_GALLERY_LABEL} into Events or one of the seven college folders. That name appears on the public Gallery card.`;
  const accept = isCirculars ? "application/pdf,.pdf,.doc,.docx,image/*" : "image/*";
  const testId = isCirculars ? "circulars-admin" : "gallery-admin";
  const currentFolder = options.find((o) => o.value === choice) || options[0];
  const folderPath = currentFolder.folder || currentFolder.label;
  const visibleFiles = files.filter((f) => {
    if (isCirculars) {
      const cat = String(f.category || "").toLowerCase();
      if (choice === "circular") return !cat || cat === "circular";
      return cat === choice;
    }
    return String(f.album || "").toLowerCase() === choice;
  });

  return (
    <TiltCard intensity={2}>
      <div style={{ padding: 26 }} data-testid={testId}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: "#1C1917", display: "flex", alignItems: "center", gap: 8 }}>
            {isCirculars ? <FileText size={20} color="#F97316" /> : <ImageIcon size={20} color="#F97316" />}
            {heading}
          </div>
          <button type="button" onClick={load} className="btn-outline-3d" style={{ padding: "6px 12px", fontSize: 12 }} data-testid={`${testId}-refresh`}>
            <RefreshCw size={12} /> Refresh
          </button>
        </div>
        <div className="drive-folder-row" data-testid={`${testId}-folders`} style={{ marginTop: 12 }}>
          {isCirculars && options.map((o) => (
            <button
              key={o.value}
              type="button"
              data-testid={`${testId}-folder-${o.value}`}
              className={choice === o.value ? "drive-folder-chip drive-folder-chip-active" : "drive-folder-chip"}
              onClick={() => setChoice(o.value)}
            >
              {o.folder || o.label}
            </button>
          ))}
          {!isCirculars && (
            <button
              type="button"
              data-testid={`${testId}-folder-events`}
              className={choice === "events" ? "drive-folder-chip drive-folder-chip-active" : "drive-folder-chip"}
              onClick={() => setChoice("events")}
            >
              Events
            </button>
          )}
          <select
            data-testid={`${testId}-folder`}
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            className="input-3d"
            style={{ width: "auto", padding: "8px 12px", minWidth: 220 }}
            aria-label="Drive folder"
          >
            {options.map((o) => <option key={o.value} value={o.value}>{o.folderName || o.folder || o.label}</option>)}
          </select>
          {!isCirculars && allowEdit && (
            <button
              type="button"
              onClick={() => syncWebsite({ silent: false })}
              className="btn-outline-3d"
              style={{ padding: "6px 12px", fontSize: 12 }}
              disabled={busy || !drive.connected}
              data-testid={`${testId}-sync-website`}
            >
              Sync website photos
            </button>
          )}
        </div>
        <div className="drive-folder-using" data-testid={`${testId}-folder-label`}>
          <FolderOpen size={14} style={{ verticalAlign: "middle", marginRight: 6 }} />
          Using Drive folder: {folderPath}
        </div>
        <p style={{ fontSize: 13.5, color: "#57534E", marginTop: 8, lineHeight: 1.6 }}>
          {allowEdit ? help : "Your account is set to view only. You can see these files. The administrator must grant edit access before you can upload or remove them."}
        </p>

        {!allowEdit && (
          <div data-testid={`${testId}-view-only`} style={{ marginTop: 12, padding: "12px 14px", background: "#FFF7ED", border: "1px solid rgba(249,115,22,0.25)", borderRadius: 12, color: "#9A3412", fontSize: 13, fontWeight: 600 }}>
            View access — upload and delete stay locked.
          </div>
        )}

        {drive.error && (
          <div data-testid={`${testId}-disconnected`} style={{ marginTop: 12, padding: "12px 14px", background: drive.connected ? "#FFF7ED" : "#FEF2F2", border: `1px solid ${drive.connected ? "rgba(249,115,22,0.3)" : "rgba(220,38,38,0.3)"}`, borderRadius: 12, color: drive.connected ? "#9A3412" : "#B91C1C", fontSize: 13, display: "flex", gap: 8 }}>
            <AlertCircle size={16} style={{ flexShrink: 0 }} />
            {drive.error}{drive.connected || isTransientServerError(drive.error) ? "" : ". Add the Google service account on Render (not Amplify) and share the Drive folder with that email as Editor. See DRIVE-SETUP.md."}
          </div>
        )}

        {allowEdit && (
          <input
            data-testid={`${testId}-title`}
            className="input-3d"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder={isCirculars ? "Title shown on the public Circulars page" : "Name tag shown on the public Gallery card"}
            style={{ marginTop: 14 }}
          />
        )}

        {allowEdit && (
          <label className="btn-primary-3d" style={{ marginTop: 12, cursor: busy ? "progress" : "pointer", opacity: busy || !drive.connected ? 0.7 : 1 }} data-testid={`${testId}-upload-label`}>
            <Upload size={15} /> {busy ? "Uploading…" : (isCirculars ? "Choose circular / G.O." : "Choose photographs")}
            <input
              ref={inputRef}
              data-testid={`${testId}-upload`}
              type="file"
              multiple
              accept={accept}
              onChange={upload}
              disabled={busy || !drive.connected}
              style={{ display: "none" }}
            />
          </label>
        )}

        {msg && (
          <div data-testid={`${testId}-msg`} style={{ marginTop: 12, fontSize: 13.5, fontWeight: 600, color: msg.ok ? "#15803D" : "#B91C1C", display: "flex", gap: 6 }}>
            {msg.ok ? <CheckCircle2 size={16} /> : <AlertCircle size={16} />} {msg.text}
          </div>
        )}

        <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1, color: "#EA580C", marginTop: 22 }}>
          FILES IN {folderPath.toUpperCase()} ({status === "loading" ? "…" : visibleFiles.length})
        </div>
        {status === "loading" && (
          <div data-testid={`${testId}-loading`} style={{ marginTop: 10, fontSize: 13, color: "#78716C" }}>Loading files…</div>
        )}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(160px,1fr))", gap: 10, marginTop: 10 }} data-testid={`${testId}-list`}>
          {status === "ready" && visibleFiles.map((f) => (
            <div key={f.id} data-testid={`${testId}-item-${f.id}`} style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(249,115,22,0.25)", background: "#fff", display: "flex", flexDirection: "column" }}>
              <a href={f.view || f.viewLink || f.url} target="_blank" rel="noreferrer" style={{ textDecoration: "none", color: "#1C1917" }}>
                {f.isImage || f.kind === "photo" ? (
                  <img src={f.thumb || f.url} alt={f.title || f.name} style={{ width: "100%", aspectRatio: "4/3", objectFit: "cover" }} />
                ) : (
                  <div style={{ aspectRatio: "4/3", display: "flex", alignItems: "center", justifyContent: "center", background: "#FFF7ED" }}>
                    <FileText size={30} color="#F97316" />
                  </div>
                )}
                <div style={{ fontSize: 11, padding: "8px 8px 0", fontWeight: 700, color: "#EA580C" }}>{f.type || f.category || f.album}</div>
                <div style={{ fontSize: 12, padding: "4px 8px 8px", fontWeight: 600, lineHeight: 1.35 }}>{f.title || f.name}</div>
              </a>
              {allowEdit && (
                <button
                  type="button"
                  className="btn-outline-3d"
                  data-testid={`${testId}-delete-${f.id}`}
                  onClick={() => remove(f)}
                  disabled={busy || !drive.connected}
                  style={{ margin: "0 8px 10px", padding: "7px 10px", fontSize: 12, color: "#B91C1C" }}
                >
                  <Trash2 size={12} /> Remove
                </button>
              )}
            </div>
          ))}
          {status === "ready" && !visibleFiles.length && (
            <div style={{ fontSize: 13, color: "#78716C", gridColumn: "1/-1" }}>
              {isCirculars ? `No files in ${folderPath} yet.` : `No photographs in ${folderPath} yet.`}
            </div>
          )}
        </div>
      </div>
    </TiltCard>
  );
}
